import React, { useState, useEffect } from 'react';
import { IconSearch, IconArrowLeft } from '@tabler/icons-react';
import orderImg from '../../assets/images/fashion-hoodie.jpg';
import { useNavigate } from 'react-router-dom';
import { useSelector, useDispatch } from 'react-redux';
import { fetchOrderHistory, userOrderHistorySearchAndFilter, userMakesOrderAsDelivered, userAddOrUpdateOrderFeedback } from '../../redux/slices/client/clientOrderSlice';
import { IconStarFilled, IconStar } from '@tabler/icons-react';

// Shimmer Effect Component
const ShimmerEffect = ({ width, height, className }) => {
  return (
    <div
      className={`animate-pulse bg-gray-200 ${className}`}
      style={{ width, height }}
    ></div>
  );
};

const FeedbackSection = ({ order, onFeedbackSubmit }) => {
  const [rating, setRating] = useState(order.feedback?.rating || 0);
  const [feedbackText, setFeedbackText] = useState(order.feedback?.feedback || '');
  const [isSubmitting, setIsSubmitting] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);
    try {
      await onFeedbackSubmit({
        order_uid: order.order_uid,
        rating,
        feedback: feedbackText
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="mt-8 pt-6 border-t border-gray-200">
      <h3 className="text-xl font-bold mb-4 text-[#00148c]">Your Feedback</h3>

      <form onSubmit={handleSubmit}>
        <div className="mb-4">
          <label className="block text-sm font-medium text-gray-700 mb-2">Rating</label>
          <div className="flex gap-1">
            {[1, 2, 3, 4, 5].map((star) => (
              <button
                key={star}
                type="button"
                onClick={() => setRating(star)}
                className="text-2xl focus:outline-none"
              >
                {star <= rating ? (
                  <IconStarFilled className="text-yellow-400" />
                ) : (
                  <IconStar className="text-gray-300" />
                )}
              </button>
            ))}
          </div>
        </div>

        <div className="mb-4">
          <label className="block text-sm font-medium text-gray-700 mb-2">Feedback</label>
          <textarea
            value={feedbackText}
            onChange={(e) => setFeedbackText(e.target.value)}
            className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-[#00148c]"
            rows="4"
            placeholder="Share your experience with this order..."
            required
          />
        </div>

        <button
          type="submit"
          disabled={isSubmitting || rating === 0}
          className={`px-4 py-2 rounded-md text-white ${isSubmitting || rating === 0 ? 'bg-gray-400 cursor-not-allowed' : 'bg-[#00148c] hover:bg-[#002080]'}`}
        >
          {isSubmitting ? 'Submitting...' : order.feedback ? 'Update Feedback' : 'Submit Feedback'}
        </button>
      </form>
    </div>
  );
};


// Order Detail Component
const OrderDetailView = ({ order: initialOrder, onBack, navigate }) => {
  const [currentView, setCurrentView] = useState('front');
  const [showMarkAsDeliveredConfirm, setShowMarkAsDeliveredConfirm] = useState(false);
  const [isMarkingAsDelivered, setIsMarkingAsDelivered] = useState(false);
  const dispatch = useDispatch();

  // const dispatch = useDispatch();
  // Get the latest order data from Redux
  const { userOrderHistory } = useSelector((state) => state.userOrder);

  // Find the current order in Redux state
  const order = userOrderHistory.find(
    o => String(o.order_number) === String(initialOrder.order_number)
  ) || initialOrder;

  const previewWidth = 400;
  const previewHeight = 300;

  const reconstructLayers = (design, containerWidth, containerHeight) => {
    if (!design?.layers) return { frontLayers: [], backLayers: [] };

    const originalDesignWidth = 500;
    const originalDesignHeight = 400;
    const scaleX = containerWidth / originalDesignWidth;
    const scaleY = containerHeight / originalDesignHeight;

    const processLayer = (layer) => {
      const baseLayer = {
        id: layer.id,
        type: layer.type,
        x: layer.x * scaleX,
        y: layer.y * scaleY,
        width: layer.width * scaleX,
        height: layer.height * scaleY,
        rotation: layer.rotation,
        flipped: layer.flipped,
        visible: layer.visible,
        zIndex: layer.z_index,
        borderColor: layer.border_color,
        borderStyle: layer.border_style,
        offsetX: layer.offset_x * scaleX,
        offsetY: layer.offset_y * scaleY,
      };

      if (layer.type === 'image' && layer.image_data) {
        return {
          ...baseLayer,
          src: layer.image_data.original_src || layer.image_data.src || '',
        };
      } else if (layer.type === 'text' && layer.text_data) {
        return {
          ...baseLayer,
          text: layer.text_data.text,
          color: layer.text_data.color,
        };
      }
      return baseLayer;
    };

    const frontLayers = design.layers.filter((l) => l.side === 'front').map(processLayer);
    const backLayers = design.layers.filter((l) => l.side === 'back').map(processLayer);

    return { frontLayers, backLayers };
  };

  const handleMarkAsDelivered = async () => {
    setIsMarkingAsDelivered(true);
    try {
      const orderId = order.order_number;
      await dispatch(userMakesOrderAsDelivered(orderId)).unwrap();
      // await dispatch(userMakesOrderAsDelivered(order.order_number)).unwrap();
    } catch (error) {
      console.error('Failed to mark as delivered:', error);
    } finally {
      setIsMarkingAsDelivered(false);
      setShowMarkAsDeliveredConfirm(false);
    }
  };
  const handleFeedbackSubmit = async (feedbackData) => {
    try {

      await dispatch(userAddOrUpdateOrderFeedback({
        orderId: order.order_number,
        formData: {
          rating: feedbackData.rating,
          feedback: feedbackData.feedback
        }
      })).unwrap();

      // Optionally show a success message here
    } catch (error) {
      console.error('Failed to submit feedback:', error);
    }
  };
  const getImageSrc = () => {
    if (order.type === 'product') {
      return order.item_detail?.product_detail?.template?.media_files?.[0]?.front_image || '';
    } else if (order.type === 'project') {
      return order.item_detail?.product_detail?.product?.images?.front_image || '';
    }
    return '';
  };

  const getBackImageSrc = () => {
    if (order.type === 'product') {
      return order.item_detail?.product_detail?.template?.media_files?.[0]?.back_image || '';
    } else if (order.type === 'project') {
      return order.item_detail?.product_detail?.product?.images?.back_image || '';
    }
    return '';
  };

  const { frontLayers, backLayers } = order.type === 'project'
    ? reconstructLayers(order.item_detail.product_detail, previewWidth, previewHeight)
    : { frontLayers: [], backLayers: [] };

  const layers = currentView === 'back' ? backLayers : frontLayers;
  const imageSrc = currentView === 'back' ? getBackImageSrc() : getImageSrc();


  return (
    <div className="bg-white p-6 rounded-lg shadow-sm">
      <button
        onClick={onBack}
        className="flex items-center gap-2 mb-6 text-[#00148c] hover:text-[#002080]"
      >
        <IconArrowLeft size={20} />
        <span>Back to Orders</span>
      </button>

      <div className="flex flex-col lg:flex-row gap-8">
        {/* Preview Section */}
        <div className="w-full lg:w-1/2">
          <h2 className="text-2xl font-bold mb-4 text-[#00148c]">
            {order.type === 'product'
              ? order.item_detail?.product_detail?.product_name
              : order.item_detail?.product_detail?.name}
          </h2>

          {/* Preview Pane - Updated with new logic */}
          <div className="mb-6">
            <div className="relative flex justify-center items-center mb-4">
              {/* Preview Container - Matches Design.jsx preview dimensions (400x300) */}
              <div className="relative w-[400px] h-[300px] bg-white shadow-md mx-auto shadow-lg rounded-md shadow-[#0B0C52]">
                {order.type === 'project' ? (
                  <div className="relative w-full h-full">
                    {/* Front View */}
                    {currentView === 'front' && (
                      <div className="absolute w-full h-full">
                        {imageSrc && (
                          <img
                            src={imageSrc}
                            alt="Product front"
                            className="absolute w-full h-full object-contain"
                            style={{ zIndex: 1 }}
                          />
                        )}
                        {layers.map((layer) => (
                          layer.visible && (
                            <div
                              key={layer.id}
                              className="absolute"
                              style={{
                                top: `${layer.y}px`,
                                left: `${layer.x}px`,
                                width: `${layer.width}px`,
                                height: `${layer.height}px`,
                                transform: `rotate(${layer.rotation}deg) scaleX(${layer.flipped ? -1 : 1})`,
                                zIndex: layer.zIndex + 2,
                                overflow: 'hidden',
                              }}
                            >
                              {layer.type === 'text' ? (
                                <div
                                  className="w-full h-full flex items-center justify-center p-1"
                                  style={{
                                    color: layer.color,
                                    fontWeight: 'bold',
                                    borderRadius: '2px',
                                  }}
                                >
                                  {layer.text}
                                </div>
                              ) : (
                                layer.src && (
                                  <img
                                    src={layer.src}
                                    alt="Layer"
                                    className="w-full h-full object-cover"
                                  />
                                )
                              )}
                            </div>
                          )
                        ))}
                      </div>
                    )}

                    {/* Back View */}
                    {currentView === 'back' && (
                      <div className="absolute w-full h-full">
                        {getBackImageSrc() && (
                          <img
                            src={getBackImageSrc() || imageSrc}
                            alt="Product back"
                            className="absolute w-full h-full object-contain"
                            style={{ zIndex: 1 }}
                          />
                        )}
                        {layers.map((layer) => (
                          layer.visible && (
                            <div
                              key={layer.id}
                              className="absolute"
                              style={{
                                top: `${layer.y}px`,
                                left: `${layer.x}px`,
                                width: `${layer.width}px`,
                                height: `${layer.height}px`,
                                transform: `rotate(${layer.rotation}deg) scaleX(${layer.flipped ? -1 : 1})`,
                                zIndex: layer.zIndex + 2,
                                overflow: 'hidden',
                              }}
                            >
                              {layer.type === 'text' ? (
                                <div
                                  className="w-full h-full flex items-center justify-center p-1"
                                  style={{
                                    color: layer.color,
                                    fontWeight: 'bold',
                                    borderRadius: '2px',
                                  }}
                                >
                                  {layer.text}
                                </div>
                              ) : (
                                layer.src && (
                                  <img
                                    src={layer.src}
                                    alt="Layer"
                                    className="w-full h-full object-cover"
                                  />
                                )
                              )}
                            </div>
                          )
                        ))}
                      </div>
                    )}
                  </div>
                ) : (
                  <div className="relative w-full h-full bg-gray-100 rounded-md overflow-hidden">
                    <div className={`flipper ${currentView === 'back' ? 'flipped' : ''}`}>
                      {/* Front View */}
                      <div className="front absolute w-full h-full">
                        {getImageSrc() ? (
                          <img
                            src={getImageSrc()}
                            alt={order.item_detail?.product_detail?.product_name}
                            className="w-full h-full object-contain"
                          />
                        ) : (
                          <div className="w-full h-full flex items-center justify-center">
                            <p className="text-gray-500">No preview available</p>
                          </div>
                        )}
                      </div>

                      {/* Back View */}
                      <div className="back absolute w-full h-full">
                        {getBackImageSrc() ? (
                          <img
                            src={getBackImageSrc()}
                            alt={`${order.item_detail?.product_detail?.product_name} (back)`}
                            className="w-full h-full object-contain"
                          />
                        ) : (
                          <div className="w-full h-full flex items-center justify-center">
                            <p className="text-gray-500">No preview available</p>
                          </div>
                        )}
                      </div>
                    </div>
                  </div>
                )}
              </div>
            </div>

            {/* Front/Back Toggle - Matches Design.jsx styling */}
            <div className="flex justify-center gap-2">
              <button
                className={`px-6 py-2 rounded-full text-sm font-medium transition-all duration-300 ${currentView === 'front'
                  ? 'bg-[#0B0C52] text-white shadow-md transform scale-105'
                  : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
                  }`}
                onClick={() => setCurrentView('front')}
              >
                <span className="flex items-center justify-center gap-1">
                  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor">
                    <path d="M21 12H3M12 21V3M12 21L3 12M12 21L21 12M12 3L3 12M12 3L21 12" strokeWidth="2" strokeLinecap="round" />
                  </svg>
                  Front
                </span>
              </button>
              <button
                className={`px-6 py-2 rounded-full text-sm font-medium transition-all duration-300 ${currentView === 'back'
                  ? 'bg-[#0B0C52] text-white shadow-md transform scale-105'
                  : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
                  }`}
                onClick={() => setCurrentView('back')}
              >
                <span className="flex items-center justify-center gap-1">
                  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor">
                    <path d="M12 3H3V21H21V12M12 21V3M12 21L21 12M12 3L21 12" strokeWidth="2" strokeLinecap="round" />
                  </svg>
                  Back
                </span>
              </button>
            </div>
          </div>

          {/* Product/Project Details */}
          <div className="mb-6">
            <div className="flex items-center gap-3">
              {/* Icon based on project/product type */}
              {order.type === 'project' ? (
                <svg className="w-6 h-6 text-[#0B0C52]" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2" />
                </svg>
              ) : (
                <svg className="w-6 h-6 text-[#0B0C52]" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M20 7l-8-4-8 4m16 0l-8 4m8-4v10l-8 4m0-10L4 7m8 4v10M4 7v10l8 4" />
                </svg>
              )}

              <h2 className="text-3xl font-bold text-gray-800 tracking-tight">
                {order.type === 'project'
                  ? order.item_detail?.product_detail?.name || 'My Project'
                  : order.item_detail?.product_detail?.product_name || 'Our Product'}
              </h2>
            </div>

            {/* Subtitle with category */}
            <p className="mt-1 text-lg text-gray-500">
              {order.type === 'project' ? (
                <>
                  Custom {order.item_detail?.product_detail?.product?.category?.name.toLowerCase() || 'design'} •
                  Created {order.item_detail?.product_detail?.created_at ? new Date(order.item_detail.product_detail.created_at).toLocaleDateString() : 'recently'}
                </>
              ) : (
                <>
                  {order.item_detail?.product_detail?.category?.name || 'Product'} •
                  {order.item_detail?.product_detail?.sub_category?.name || 'Item'}
                </>
              )}
            </p>
          </div>


        </div>
        <div className="w-full lg:w-1/2">
          <div className="bg-gray-50 p-6 rounded-lg">
            <h3 className="text-xl font-bold mb-4 text-[#00148c]">Order Details</h3>

            <div className="space-y-4">
              <div>
                <h4 className="font-medium text-gray-700">Order Information</h4>
                <div className="grid grid-cols-2 gap-2 mt-2 text-sm">
                  <div>
                    <p className="text-gray-500">Order Number:</p>
                    <p className="font-medium">{order.order_number}</p>
                  </div>
                  <div>
                    <p className="text-gray-500">Order Date:</p>
                    <p className="font-medium">
                      {new Date(order.created_at).toLocaleDateString()}
                    </p>
                  </div>
                  <div>
                    <p className="text-gray-500">Status:</p>
                    <p className="font-medium">{order.status}</p>
                  </div>
                  <div>
                    <p className="text-gray-500">Estimated Delivery:</p>
                    <p className="font-medium">
                      {order.estimated_delivery_date === '--/--/----'
                        ? 'To be determined'
                        : order.estimated_delivery_date}
                    </p>
                  </div>
                  <div>
                    <p className="text-gray-500">Total Amount:</p>
                    <p className="font-medium">${order.total_amount}</p>
                  </div>
                </div>
              </div>

              <div>
                <h4 className="font-medium text-gray-700">Product Specifications</h4>
                <div className="grid grid-cols-2 gap-2 mt-2 text-sm">
                  {order.item_detail?.item_specification?.color && (
                    <div>
                      <p className="text-gray-500">Color:</p>
                      <p className="font-medium">
                        {order.item_detail.item_specification.color.join(', ')}
                      </p>
                    </div>
                  )}
                  {order.item_detail?.item_specification?.size && (
                    <div>
                      <p className="text-gray-500">Size:</p>
                      <p className="font-medium">
                        {order.item_detail.item_specification.size.join(', ')}
                      </p>
                    </div>
                  )}
                  {order.item_detail?.item_specification?.fabric_material && (
                    <div>
                      <p className="text-gray-500">Material:</p>
                      <p className="font-medium">
                        {order.item_detail.item_specification.fabric_material.join(', ')}
                      </p>
                    </div>
                  )}
                  {order.item_detail?.item_specification?.printing_method && (
                    <div>
                      <p className="text-gray-500">Printing Method:</p>
                      <p className="font-medium">
                        {order.item_detail.item_specification.printing_method.join(', ')}
                      </p>
                    </div>
                  )}
                  {order.item_detail?.item_specification?.customization_options && (
                    <div>
                      <p className="text-gray-500">Customization:</p>
                      <p className="font-medium">
                        {order.item_detail.item_specification.customization_options.join(', ')}
                      </p>
                    </div>
                  )}
                </div>
              </div>

              {order.type === 'product' && order.item_detail?.product_detail && (
                <div>
                  <h4 className="font-medium text-gray-700">Product Details</h4>
                  <div className="grid grid-cols-2 gap-2 mt-2 text-sm">
                    <div>
                      <p className="text-gray-500">Category:</p>
                      <p className="font-medium">
                        {order.item_detail.product_detail.category?.name}
                      </p>
                    </div>
                    <div>
                      <p className="text-gray-500">Subcategory:</p>
                      <p className="font-medium">
                        {order.item_detail.product_detail.sub_category?.name}
                      </p>
                    </div>
                    <div>
                      <p className="text-gray-500">Price:</p>
                      <p className="font-medium">
                        ${order.item_detail.product_detail.price}
                      </p>
                    </div>
                    <div>
                      <p className="text-gray-500">Stock Level:</p>
                      <p className="font-medium">
                        {order.item_detail.product_detail.stock_level}
                      </p>
                    </div>
                  </div>
                </div>
              )}

              {order.type === 'project' && order.item_detail?.product_detail && (
                <div>
                  <h4 className="font-medium text-gray-700">Project Details</h4>
                  <div className="grid grid-cols-2 gap-2 mt-2 text-sm">
                    <div>
                      <p className="text-gray-500">Name:</p>
                      <p className="font-medium">
                        {order.item_detail.product_detail.name}
                      </p>
                    </div>
                    <div>
                      <p className="text-gray-500">Created:</p>
                      <p className="font-medium">
                        {new Date(order.item_detail.product_detail.created_at).toLocaleDateString()}
                      </p>
                    </div>
                    <div>
                      <p className="text-gray-500">Layers:</p>
                      <p className="font-medium">
                        {order.item_detail.product_detail.layers?.length || 0}
                      </p>
                    </div>
                  </div>
                </div>
              )}
            </div>

            <div className="mt-6 pt-4 border-t border-gray-200">
              <div className={`grid ${order.status === 'Out for Delivery' ? 'grid-cols-2' : 'grid-cols-1'} gap-4`}>
                <button
                  className="w-full bg-[#00148c] text-white py-3 px-4 rounded-lg hover:bg-[#002080] 
                transition-colors font-medium shadow-md hover:shadow-lg
                flex items-center justify-center gap-2"
                  onClick={() => navigate(`/track-order/${order.order_number}`)}
                >
                  <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                  </svg>
                  Track Order
                </button>

                {order.status === 'Out for Delivery' && (
                  <button
                    onClick={() => setShowMarkAsDeliveredConfirm(true)}
                    className="w-full bg-green-600 text-white py-3 px-4 rounded-lg hover:bg-green-700 
                  transition-colors font-medium shadow-md hover:shadow-lg
                  flex items-center justify-center gap-2"
                  >
                    <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                    </svg>
                    Mark as Delivered
                  </button>
                )}
              </div>
            </div>

          </div>
        </div>
      </div>

      {/* {order.status === 'Out for Delivery' && (
        <div className="mt-6 pt-4 border-t border-gray-200">
          <button
            onClick={() => setShowMarkAsDeliveredConfirm(true)}
            className="w-full bg-[#00148c] text-white py-3 px-4 rounded-lg hover:bg-[#002080] 
                    transition-colors font-medium shadow-md hover:shadow-lg
                    flex items-center justify-center gap-2"
          >
            <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
            </svg>
            Mark as Delivered
          </button>
        </div>
      )} */}

      {order.status === 'Delivered' && (
        <FeedbackSection
          order={order}
          onFeedbackSubmit={handleFeedbackSubmit}
        />
      )}

      {showMarkAsDeliveredConfirm && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white p-6 rounded-lg max-w-md w-full">
            <h3 className="text-xl font-bold mb-4">Confirm Delivery</h3>
            <p className="mb-6">
              Are you sure you want to mark this order as delivered? This action is irreversible
              and will enable the feedback system for this order.
            </p>
            <div className="flex justify-end gap-3">
              <button
                onClick={() => setShowMarkAsDeliveredConfirm(false)}
                className="px-4 py-2 border border-gray-300 rounded-md hover:bg-gray-50"
              >
                Cancel
              </button>
              <button
                onClick={handleMarkAsDelivered}
                disabled={isMarkingAsDelivered}
                className={`px-4 py-2 rounded-md text-white ${isMarkingAsDelivered ? 'bg-green-400' : 'bg-green-600 hover:bg-green-700'}`}
              >
                {isMarkingAsDelivered ? 'Processing...' : 'Confirm Delivery'}
              </button>
            </div>
          </div>
        </div>
      )}

    </div>
  );
};

export default function OrderHistory() {
  const [selectedOrder, setSelectedOrder] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [dateRange, setDateRange] = useState('');
  const [selectedStatuses, setSelectedStatuses] = useState([]);
  const [selectedTypes, setSelectedTypes] = useState([]);
  const [filteredOrders, setFilteredOrders] = useState(null);

  const dispatch = useDispatch();
  const navigate = useNavigate();

  const { userOrderHistory, userOrderHistoryLoading: loading, userOrderHistoryError } = useSelector((state) => state.userOrder);

  // Pagination state
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage] = useState(6);

  useEffect(() => {
    dispatch(fetchOrderHistory());
  }, [dispatch]);



  // Calculate pagination
  const currentItemsData = filteredOrders || userOrderHistory || [];
  const indexOfLastItem = currentPage * itemsPerPage;
  const indexOfFirstItem = indexOfLastItem - itemsPerPage;
  const currentItems = currentItemsData.slice(indexOfFirstItem, indexOfLastItem);
  const totalPages = Math.ceil((currentItemsData.length || 0) / itemsPerPage);
  const applyFilters = () => {
    const filters = {
      searchTerm,
      dateRange,
      selectedStatuses,
      selectedTypes,
    };
    const noFiltersSelected =
      !searchTerm && !dateRange && selectedStatuses.length === 0 && selectedTypes.length === 0;

    if (noFiltersSelected) {
      // Fetch the full order history if no filters are selected
      dispatch(fetchOrderHistory());
    } else {
      // Dispatch the filtering action
      dispatch(userOrderHistorySearchAndFilter(filters)).unwrap();
    }
  };

  // Handle clearing filters
  const handleClearFilters = () => {
    setSearchTerm('');
    setDateRange('');
    setSelectedStatuses([]);
    setSelectedTypes([]);
    setFilteredOrders(null);
    setCurrentPage(1);
    dispatch(fetchOrderHistory());
  };

  const handleReorder = (order) => {
    const selectedItemTypeProject = order.type === 'project';
    const pathToNavigateToPass = [
      {
        'name': 'Home',
        'path': '/'
      },
      {
        'name': 'Order History',
        'path': `/orders`
      }

    ];

    if (selectedItemTypeProject) {
      navigate(`/checkout/${order.product_id}/${selectedItemTypeProject}/${order.item_detail.product_detail.id}`, { state: pathToNavigateToPass });
    } else {
      navigate(`/checkout/${order.product_id}/${selectedItemTypeProject}`, { state: pathToNavigateToPass });
    }

  }
  // Change page
  const paginate = (pageNumber) => setCurrentPage(pageNumber);

  // Handle view details
  const handleViewDetails = (order) => {
    setSelectedOrder(order);
  };

  // Handle back to list
  const handleBackToList = () => {
    setSelectedOrder(null);
  };

  if (selectedOrder) {
    return (
      <div className="bg-[#EEE8E8] min-h-screen py-8 relative">
        <div className="max-w-screen-xl mx-auto px-4">
          <OrderDetailView order={selectedOrder} onBack={handleBackToList} navigate={navigate} />
        </div>
      </div>
    );
  }

  return (
    <div className="bg-[#EEE8E8] min-h-screen py-8">
      <div className="max-w-screen-xl mx-auto px-4">
        {/* Page Title */}
        <div className="flex justify-between items-center mb-8">
          <h1 className="text-3xl font-bold text-[#00148c]">Order History</h1>
        </div>

        <div className="flex flex-col lg:flex-row gap-8">
          {/* LEFT SIDEBAR: Filters & Sorting */}
          <div className="w-full lg:w-64 bg-white p-6 rounded-lg shadow-sm h-fit sticky top-8">
            <h2 className="text-lg font-bold mb-6 text-[#00148c]">Filters & Sorting</h2>

            {/* Search Bar */}
            <div className="mb-6">
              <label className="block text-sm font-medium text-gray-700 mb-2">Search by Order Number</label>
              <div className="flex items-center border rounded-md px-3 py-2">
                <IconSearch size={16} className="text-gray-400 mr-2" />
                <input
                  type="text"
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  placeholder="e.g. 3e59e600..."
                  className="flex-1 focus:outline-none text-sm"
                />
              </div>
            </div>

            {/* Date Range */}
            <div className="mb-6">
              <h3 className="text-sm font-medium text-gray-700 mb-2">Date Range</h3>
              <div className="space-y-2">
                {['Last 30 days', 'Last 90 days', 'Custom'].map((range) => (
                  <label key={range} className="flex items-center gap-2">
                    <input
                      type="radio"
                      name="date-range"
                      className="accent-[#00148c]"
                      checked={dateRange === range}
                      onChange={() => setDateRange(range)}
                    />
                    {range}
                  </label>
                ))}
              </div>
            </div>

            {/* Order Status */}
            <div className="mb-6">
              <h3 className="text-sm font-medium text-gray-700 mb-2">Order Status</h3>
              <div className="space-y-2 max-h-48 overflow-y-auto">
                {[
                  'Ordering',
                  'Order Placed',
                  'Pending',
                  'Shipped',
                  'Out for Delivery',
                  'Delivered',
                  'Cancelled',
                  'Refunded',
                ].map((status) => (
                  <label key={status} className="flex items-center gap-2 cursor-pointer">
                    <input
                      type="checkbox"
                      className="accent-[#00148c]"
                      checked={selectedStatuses.includes(status)}
                      onChange={() => {
                        setSelectedStatuses((prev) =>
                          prev.includes(status)
                            ? prev.filter((s) => s !== status)
                            : [...prev, status]
                        );
                      }}
                    />
                    {status}
                  </label>
                ))}
              </div>
            </div>

            {/* Product Type */}
            <div className="mb-6">
              <h3 className="text-sm font-medium text-gray-700 mb-2">Item Type</h3>
              <div className="space-y-2 max-h-48 overflow-y-auto">
                {['product', 'project'].map((type) => (
                  <label key={type} className="flex items-center gap-2 cursor-pointer">
                    <input
                      type="checkbox"
                      className="accent-[#00148c]"
                      checked={selectedTypes.includes(type)}
                      onChange={() => {
                        setSelectedTypes((prev) =>
                          prev.includes(type)
                            ? prev.filter((t) => t !== type)
                            : [...prev, type]
                        );
                      }}
                    />
                    {type.charAt(0).toUpperCase() + type.slice(1)}
                  </label>
                ))}
              </div>
            </div>

            {/* Apply and Clear Filters */}
            <div className="flex flex-col gap-3 mt-8">
              <button
                onClick={applyFilters}
                className="w-full bg-[#00148c] text-white font-medium py-2 px-4 rounded-lg hover:bg-[#002080] transition-colors"
              >
                Apply Filters
              </button>
              <button
                onClick={handleClearFilters}
                className="w-full bg-gray-100 text-gray-700 font-medium py-2 px-4 rounded-lg hover:bg-gray-200 transition-colors"
              >
                Clear Filters
              </button>
            </div>
          </div>

          {/* RIGHT COLUMN: Orders List */}
          <div className="flex-1">
            {/* Loading state */}
            {loading && (
              <div className="space-y-4">
                {/* Shimmer for title */}
                <ShimmerEffect width="50%" height="24px" className="rounded-md" />
                {/* Shimmer for order cards */}
                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
                  {[...Array(6)].map((_, index) => (
                    <div key={index} className="bg-white rounded-md p-4 shadow-sm">
                      <ShimmerEffect width="100%" height="150px" className="rounded-md mb-4" />
                      <ShimmerEffect width="80%" height="20px" className="rounded-md mb-2" />
                      <ShimmerEffect width="60%" height="16px" className="rounded-md mb-2" />
                      <ShimmerEffect width="70%" height="16px" className="rounded-md mb-2" />
                      <ShimmerEffect width="50%" height="16px" className="rounded-md mb-3" />
                      <ShimmerEffect width="100%" height="36px" className="rounded-md" />
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* Empty state */}
            {!loading && currentItemsData.length === 0 && (
              <div className="text-center text-gray-600">
                <p className="text-lg font-medium">No orders found.</p>
                <p>Try adjusting your filters.</p>
              </div>
            )}

            {/* Orders grid */}
            {!loading && currentItemsData.length > 0 && (
              <>
                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
                  {currentItems.map((order) => (
                    <div
                      key={order.order_number}
                      className="bg-white rounded-lg shadow-md overflow-hidden flex flex-col transition-all duration-200 hover:shadow-lg"
                    >


                      {/* Image with subtle frame */}
                      <div className="relative pt-[75%] bg-gray-50 overflow-hidden">
                        <div className={`absolute top-0 right-0 px-3 py-1 text-xs font-bold text-white rounded-bl-lg z-10 ${order.type === 'product' ? 'bg-blue-600' : 'bg-purple-600'
                          }`}>
                          {order.type === 'product' ? 'PRODUCT' : 'PROJECT'}
                        </div>

                        <img
                          src={
                            order.type === 'product'
                              ? order.item_detail?.product_detail?.template?.media_files?.[0]?.front_image || orderImg
                              : order.item_detail?.product_detail?.product?.images?.front_image || orderImg
                          }
                          alt={
                            order.type === 'product'
                              ? order.item_detail?.product_detail?.product_name
                              : order.item_detail?.product_detail?.name
                          }
                          className="absolute top-0 left-0 w-full h-full object-contain p-4"
                        />
                      </div>

                      {/* Order Info */}
                      <div className="p-4 flex-1 flex flex-col">
                        <div className="flex items-start justify-between mb-2">
                          <h3 className="text-lg font-semibold text-gray-800 line-clamp-2">
                            {order.type === 'product'
                              ? order.item_detail?.product_detail?.product_name
                              : order.item_detail?.product_detail?.name}
                          </h3>
                        </div>

                        <div className="space-y-1 text-sm text-gray-600 mb-4">
                          <div className="flex items-center">
                            <svg className="w-4 h-4 mr-2 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2" />
                            </svg>
                            <span>#{order.order_number}</span>
                          </div>
                          <div className="flex items-center">
                            <svg className="w-4 h-4 mr-2 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
                            </svg>
                            <span>{new Date(order.created_at).toLocaleDateString()}</span>
                          </div>
                        </div>

                        {/* Status and Price */}
                        <div className="mt-auto pt-3 border-t border-gray-100">
                          <div className="flex justify-between items-center">
                            <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${order.status === 'Delivered' ? 'bg-green-100 text-green-800' :
                              order.status === 'Shipped' ? 'bg-blue-100 text-blue-800' :
                                order.status === 'Canceled' ? 'bg-red-100 text-red-800' :
                                  'bg-yellow-100 text-yellow-800'
                              }`}>
                              {order.status}
                            </span>
                            <span className="font-bold text-gray-900">${order.total_amount}</span>
                          </div>
                        </div>

                        {/* Buttons */}
                        <div className="mt-4 grid grid-cols-2 gap-3">
                          <button
                            onClick={() => handleViewDetails(order)}
                            className="flex items-center justify-center gap-1 text-sm font-medium text-[#00148c] border border-[#00148c] rounded-md py-2 hover:bg-[#00148c] hover:text-white transition-colors"
                          >
                            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" />
                            </svg>
                            Details
                          </button>
                          <button
                            className="flex items-center justify-center gap-1 text-sm font-medium text-white bg-[#00148c] rounded-md py-2 hover:bg-[#002080] transition-colors"
                            onClick={() => handleReorder(order)}
                          >
                            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M3 3h2l.4 2M7 13h10l4-8H5.4M7 13L5.4 5M7 13l-2.293 2.293c-.63.63-.184 1.707.707 1.707H17m0 0a2 2 0 100 4 2 2 0 000-4zm-8 2a2 2 0 11-4 0 2 2 0 014 0z" />
                            </svg>
                            Reorder
                          </button>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>

                {/* Pagination Controls */}
                {totalPages > 1 && (
                  <div className="mt-8 flex justify-center">
                    <nav className="flex items-center gap-1">
                      {/* First Page */}
                      <button
                        onClick={() => paginate(1)}
                        disabled={currentPage === 1}
                        className={`px-3 py-1 rounded-md border text-sm ${currentPage === 1
                          ? 'bg-gray-100 text-gray-400 border-gray-200 cursor-not-allowed'
                          : 'bg-white text-gray-700 border-gray-300 hover:bg-gray-50'
                          }`}
                      >
                        «
                      </button>

                      {/* Previous Page */}
                      <button
                        onClick={() => paginate(currentPage - 1)}
                        disabled={currentPage === 1}
                        className={`px-3 py-1 rounded-md border text-sm ${currentPage === 1
                          ? 'bg-gray-100 text-gray-400 border-gray-200 cursor-not-allowed'
                          : 'bg-white text-gray-700 border-gray-300 hover:bg-gray-50'
                          }`}
                      >
                        ‹
                      </button>

                      {/* Current Page and Nearby Pages */}
                      {Array.from({ length: Math.min(5, totalPages) }, (_, i) => {
                        let pageNumber;
                        if (totalPages <= 5) {
                          pageNumber = i + 1;
                        } else if (currentPage <= 3) {
                          pageNumber = i + 1;
                        } else if (currentPage >= totalPages - 2) {
                          pageNumber = totalPages - 4 + i;
                        } else {
                          pageNumber = currentPage - 2 + i;
                        }

                        return (
                          <button
                            key={pageNumber}
                            onClick={() => paginate(pageNumber)}
                            className={`px-3 py-1 rounded-md border text-sm ${currentPage === pageNumber
                              ? 'bg-[#00148c] text-white border-[#00148c]'
                              : 'bg-white text-gray-700 border-gray-300 hover:bg-gray-50'
                              }`}
                          >
                            {pageNumber}
                          </button>
                        );
                      })}

                      {/* Ellipsis for many pages */}
                      {totalPages > 5 && currentPage < totalPages - 2 && (
                        <span className="px-2">...</span>
                      )}

                      {/* Last Page (if not shown) */}
                      {totalPages > 5 && currentPage < totalPages - 2 && (
                        <button
                          onClick={() => paginate(totalPages)}
                          className={`px-3 py-1 rounded-md border text-sm ${currentPage === totalPages
                            ? 'bg-[#00148c] text-white border-[#00148c]'
                            : 'bg-white text-gray-700 border-gray-300 hover:bg-gray-50'
                            }`}
                        >
                          {totalPages}
                        </button>
                      )}

                      {/* Next Page */}
                      <button
                        onClick={() => paginate(currentPage + 1)}
                        disabled={currentPage === totalPages}
                        className={`px-3 py-1 rounded-md border text-sm ${currentPage === totalPages
                          ? 'bg-gray-100 text-gray-400 border-gray-200 cursor-not-allowed'
                          : 'bg-white text-gray-700 border-gray-300 hover:bg-gray-50'
                          }`}
                      >
                        ›
                      </button>

                      {/* Last Page */}
                      <button
                        onClick={() => paginate(totalPages)}
                        disabled={currentPage === totalPages}
                        className={`px-3 py-1 rounded-md border text-sm ${currentPage === totalPages
                          ? 'bg-gray-100 text-gray-400 border-gray-200 cursor-not-allowed'
                          : 'bg-white text-gray-700 border-gray-300 hover:bg-gray-50'
                          }`}
                      >
                        »
                      </button>
                    </nav>
                  </div>
                )}
              </>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}